// pages/Dashboard/CaseDashboard.jsx

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  AlertCircle,
  Calendar,
  Download,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Users,
  FileText,
  ChevronRight,
  ChevronDown,
  X,
  Target,
} from "lucide-react";
import apiService from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatNumber = (value) => {
  if (value === null || value === undefined) return "0";
  return new Intl.NumberFormat("en-GB").format(Math.round(value));
};

const formatDecimal = (value, decimals = 1) => {
  if (value === null || value === undefined) return "0.0";
  return parseFloat(value).toFixed(decimals);
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "0%";
  return `${parseFloat(value).toFixed(1)}%`;
};

// ============================================
// HELPER: Determine group field and value from report + row data
// ============================================

const getGroupInfo = (report, memberData) => {
  const groupBy = report.config?.groupBy || report.results?.config?.groupBy;

  switch (groupBy) {
    case "ownerId":
      return {
        groupField: "owner.name",
        groupValue:
          memberData.owner?.name || memberData["owner.name"] || "Unknown",
      };
    case "accountId":
      return {
        groupField: "account.name",
        groupValue:
          memberData.account?.name || memberData["account.name"] || "Unknown",
      };
    case "type":
      return {
        groupField: "type",
        groupValue: memberData.type || "Unknown",
      };
    case "status":
      return {
        groupField: "status",
        groupValue: memberData.status || "Unknown",
      };
    case "closedDate":
      return {
        groupField: "owner.name",
        groupValue:
          memberData.owner?.name || memberData["owner.name"] || "Unknown",
      };
    default:
      if (memberData.owner?.name || memberData["owner.name"]) {
        return {
          groupField: "owner.name",
          groupValue:
            memberData.owner?.name || memberData["owner.name"] || "Unknown",
        };
      }
      return {
        groupField: "status",
        groupValue: memberData.status || "Unknown",
      };
  }
};

// ============================================
// MAIN DASHBOARD COMPONENT
// ============================================

const CasesDashboard = () => {
  const { isDarkMode } = useTheme();
  const [currentMonthPeriod, setCurrentMonthPeriod] = useState(null);
  const [supportReports, setSupportReports] = useState({});
  const [agentReports, setAgentReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [activeTab, setActiveTab] = useState("support");
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);

  const refreshIntervalRef = useRef(null);

  // Report developer names
  const SUPPORT_REPORTS = [
    "case_resolution_performance_current_month",
    "cases_by_type_priority_current_month",
    "case_status_overview_current_month",
    "day_by_day_case_closure_current_month",
    "week_by_week_case_closure_current_month",
  ];

  const AGENT_REPORTS = [
    "cases_resolved_by_agent_current_month",
    "case_volume_by_account_current_month",
    "case_response_time_current_month",
    "ytd_case_resolution_by_agent",
  ];

  // Load dashboard data
  const loadDashboardData = useCallback(
    async (showRefreshing = true, forceRefresh = false) => {
      try {
        if (showRefreshing) setRefreshing(true);
        setError(null);

        // Get current month period
        const periodResponse = await apiService.getCurrentMonthPeriod();
        setCurrentMonthPeriod(periodResponse.data);

        // Get all case reports
        const reportsResponse = await apiService.getReports({
          isPublic: true,
          reportType: "cases",
          limit: 100,
        });

        const supportData = {};
        const agentData = {};

        // Run each report with forceRefresh flag
        for (const report of reportsResponse.data) {
          try {
            if (SUPPORT_REPORTS.includes(report.developerName)) {
              const result = await apiService.runReport(
                report.id,
                {},
                forceRefresh,
              );
              supportData[report.developerName] = {
                ...report,
                results: result,
              };
            } else if (AGENT_REPORTS.includes(report.developerName)) {
              const result = await apiService.runReport(
                report.id,
                {},
                forceRefresh,
              );
              agentData[report.developerName] = {
                ...report,
                results: result,
              };
            }
          } catch (err) {
            console.error(`Failed to run report ${report.name}:`, err);
          }
        }

        setSupportReports(supportData);
        setAgentReports(agentData);
        setLastRefresh(new Date());
      } catch (err) {
        console.error("Failed to load dashboard data:", err);
        setError("Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [],
  );

  // Initial load
  useEffect(() => {
    loadDashboardData(false, false);
  }, [loadDashboardData]);

  // Set up hourly refresh
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      loadDashboardData(true, false); // Use cache for auto-refresh
    }, 3600000);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadDashboardData]);

  // Export report handler
  const handleExportReport = async (reportKey, reportType = "support") => {
    try {
      const reports = reportType === "support" ? supportReports : agentReports;
      const report = reports[reportKey];
      if (!report) return;

      const blob = await apiService.exportReport(report.id, "csv");
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.developerName}_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export failed:", err);
    }
  };

  // View details handler
  const handleViewDetails = async (report, memberData) => {
    try {
      const { groupField, groupValue } = getGroupInfo(report, memberData);

      const detailsResponse = await apiService.getGroupMemberDetails(
        report.id,
        groupField,
        groupValue,
        {},
      );

      setSelectedReport({
        ...report,
        memberData,
        detailRecords: detailsResponse.data || [],
      });
    } catch (err) {
      console.error("Failed to load details:", err);
      setSelectedReport({
        ...report,
        memberData,
        detailRecords: [],
      });
    }
  };

  // Calculate stats
  const supportStats = {
    casesClosed:
      supportReports.case_resolution_performance_current_month?.results
        ?.aggregates?.["Cases Closed"] || 0,
    avgResolutionDays:
      supportReports.case_resolution_performance_current_month?.results
        ?.aggregates?.["Avg Resolution Days"] || 0,
    highPriorityClosed:
      supportReports.case_resolution_performance_current_month?.results
        ?.aggregates?.["High Priority Closed"] || 0,
    totalCases:
      supportReports.case_status_overview_current_month?.results?.aggregates?.[
        "Total Cases"
      ] || 0,
    openCases: (() => {
      const statusReport =
        supportReports.case_status_overview_current_month?.results?.data || [];
      return statusReport
        .filter((row) => row.status !== "Closed")
        .reduce((sum, row) => sum + (parseInt(row["Total Cases"]) || 0), 0);
    })(),
    escalatedCases: (() => {
      const statusReport =
        supportReports.case_status_overview_current_month?.results?.data || [];
      const escalated = statusReport.find((row) => row.status === "Escalated");
      return escalated ? parseInt(escalated["Total Cases"]) || 0 : 0;
    })(),
  };

  // Today's closures per person from day_by_day report
  const todayClosures = (() => {
    const today = new Date().toISOString().split("T")[0];
    const data =
      supportReports.day_by_day_case_closure_current_month?.results?.data || [];
    return data.filter((row) => {
      const rowDate =
        row.closedDate instanceof Date
          ? row.closedDate.toISOString().split("T")[0]
          : String(row.closedDate || "").split("T")[0];
      return rowDate === today;
    });
  })();

  const agentStats = {
    totalClosed:
      agentReports.cases_resolved_by_agent_current_month?.results?.aggregates?.[
        "Cases Closed"
      ] || 0,
    avgResolutionDays:
      agentReports.case_response_time_current_month?.results?.aggregates?.[
        "Avg Resolution Days"
      ] || 0,
    highPriorityClosed:
      agentReports.cases_resolved_by_agent_current_month?.results?.aggregates?.[
        "High Priority"
      ] || 0,
    closedWithin1Day:
      agentReports.case_response_time_current_month?.results?.aggregates?.[
        "Closed Within 1 Day"
      ] || 0,
    closedWithin3Days:
      agentReports.case_response_time_current_month?.results?.aggregates?.[
        "Closed Within 3 Days"
      ] || 0,
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <RefreshCw
            size={48}
            className="text-blue-500 animate-spin mx-auto mb-4"
          />
          <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
            Loading case dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-3 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1
            className={`text-xl sm:text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            Case Management Dashboard
          </h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {lastRefresh && (
            <span
              className={`text-xs hidden sm:flex items-center gap-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Clock size={12} />
              Updated {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={() => loadDashboardData(true, true)} // Force refresh
            disabled={refreshing}
            className="flex items-center gap-1 sm:gap-2 px-3 sm:px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded-lg text-white text-sm transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            <span className="hidden xs:inline">
              {refreshing ? "Refreshing..." : "Refresh"}
            </span>
          </button>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Team Tabs */}
      <div
        className={`flex gap-1 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <button
          onClick={() => setActiveTab("support")}
          className={`px-3 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
            activeTab === "support"
              ? "text-blue-400 border-blue-400 bg-blue-500/5"
              : isDarkMode
                ? "text-gray-400 border-transparent hover:text-gray-300 hover:bg-gray-800/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <FileText size={16} />
            <span>Support Overview</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab("agents")}
          className={`px-3 sm:px-6 py-3 font-medium text-sm transition-colors border-b-2 -mb-px ${
            activeTab === "agents"
              ? "text-blue-400 border-blue-400 bg-blue-500/5"
              : isDarkMode
                ? "text-gray-400 border-transparent hover:text-gray-300 hover:bg-gray-800/50"
                : "text-gray-500 border-transparent hover:text-gray-700 hover:bg-gray-100"
          }`}
        >
          <div className="flex items-center gap-1 sm:gap-2">
            <Users size={16} />
            <span>Agent Performance</span>
          </div>
        </button>
      </div>

      {/* Support Dashboard */}
      {activeTab === "support" && (
        <div className="space-y-4 sm:space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
            <StatCard
              title="Cases Closed"
              value={formatNumber(supportStats.casesClosed)}
              icon={CheckCircle}
              color="green"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Avg Resolution"
              value={`${formatDecimal(supportStats.avgResolutionDays)} days`}
              icon={Clock}
              color="blue"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="High Priority"
              value={formatNumber(supportStats.highPriorityClosed)}
              icon={AlertCircle}
              color="orange"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Total Cases"
              value={formatNumber(supportStats.totalCases)}
              icon={FileText}
              color="purple"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Open Cases"
              value={formatNumber(supportStats.openCases)}
              icon={TrendingUp}
              color="cyan"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Escalated"
              value={formatNumber(supportStats.escalatedCases)}
              icon={AlertCircle}
              color="red"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Today's Closures Per Person */}
          {todayClosures.length > 0 && (
            <div>
              <h2
                className={`text-sm font-semibold uppercase tracking-wide mb-3 flex items-center gap-2 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                <CheckCircle size={14} className="text-green-400" />
                Today's Closures
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
                {todayClosures.map((row, idx) => (
                  <StatCard
                    key={idx}
                    title={row["owner.name"] || row.owner?.name || "Unknown"}
                    value={formatNumber(row["Cases Closed"])}
                    icon={CheckCircle}
                    color="green"
                    isDarkMode={isDarkMode}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {supportReports.case_resolution_performance_current_month && (
              <ReportCard
                report={
                  supportReports.case_resolution_performance_current_month
                }
                onViewDetails={(data) =>
                  handleViewDetails(
                    supportReports.case_resolution_performance_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport(
                    "case_resolution_performance_current_month",
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {supportReports.cases_by_type_priority_current_month && (
              <ReportCard
                report={supportReports.cases_by_type_priority_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    supportReports.cases_by_type_priority_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport("cases_by_type_priority_current_month")
                }
                isDarkMode={isDarkMode}
              />
            )}

            {supportReports.case_status_overview_current_month && (
              <ReportCard
                report={supportReports.case_status_overview_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    supportReports.case_status_overview_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport("case_status_overview_current_month")
                }
                isDarkMode={isDarkMode}
              />
            )}
          </div>

          {/* Day by Day Report */}
          {supportReports.day_by_day_case_closure_current_month && (
            <DayByDayReport
              report={supportReports.day_by_day_case_closure_current_month}
              onExport={() =>
                handleExportReport("day_by_day_case_closure_current_month")
              }
              isDarkMode={isDarkMode}
            />
          )}

          {/* Week by Week Report */}
          {supportReports.week_by_week_case_closure_current_month && (
            <WeekByWeekReport
              report={supportReports.week_by_week_case_closure_current_month}
              onExport={() =>
                handleExportReport("week_by_week_case_closure_current_month")
              }
              isDarkMode={isDarkMode}
            />
          )}
        </div>
      )}

      {/* Agent Performance Dashboard */}
      {activeTab === "agents" && (
        <div className="space-y-4 sm:space-y-6">
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
            <StatCard
              title="Total Closed"
              value={formatNumber(agentStats.totalClosed)}
              icon={CheckCircle}
              color="green"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="Avg Resolution"
              value={`${formatDecimal(agentStats.avgResolutionDays)} days`}
              icon={Clock}
              color="blue"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="High Priority"
              value={formatNumber(agentStats.highPriorityClosed)}
              icon={AlertCircle}
              color="orange"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="< 1 Day"
              value={formatNumber(agentStats.closedWithin1Day)}
              icon={TrendingUp}
              color="emerald"
              isDarkMode={isDarkMode}
            />
            <StatCard
              title="< 3 Days"
              value={formatNumber(agentStats.closedWithin3Days)}
              icon={Target}
              color="cyan"
              isDarkMode={isDarkMode}
            />
          </div>

          {/* Reports Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {agentReports.cases_resolved_by_agent_current_month && (
              <ReportCard
                report={agentReports.cases_resolved_by_agent_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    agentReports.cases_resolved_by_agent_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport(
                    "cases_resolved_by_agent_current_month",
                    "agents",
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {agentReports.case_volume_by_account_current_month && (
              <ReportCard
                report={agentReports.case_volume_by_account_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    agentReports.case_volume_by_account_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport(
                    "case_volume_by_account_current_month",
                    "agents",
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {agentReports.case_response_time_current_month && (
              <ReportCard
                report={agentReports.case_response_time_current_month}
                onViewDetails={(data) =>
                  handleViewDetails(
                    agentReports.case_response_time_current_month,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport(
                    "case_response_time_current_month",
                    "agents",
                  )
                }
                isDarkMode={isDarkMode}
              />
            )}

            {agentReports.ytd_case_resolution_by_agent && (
              <ReportCard
                report={agentReports.ytd_case_resolution_by_agent}
                onViewDetails={(data) =>
                  handleViewDetails(
                    agentReports.ytd_case_resolution_by_agent,
                    data,
                  )
                }
                onExport={() =>
                  handleExportReport("ytd_case_resolution_by_agent", "agents")
                }
                isDarkMode={isDarkMode}
              />
            )}
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {selectedReport && (
        <DetailModal
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
};

// ============================================
// STAT CARD COMPONENT
// ============================================

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color = "blue",
  isDarkMode,
}) => {
  const colorConfig = {
    blue: {
      bg: "from-blue-500/10 to-blue-600/5",
      border: "border-blue-500/20",
      text: "text-blue-400",
    },
    green: {
      bg: "from-green-500/10 to-green-600/5",
      border: "border-green-500/20",
      text: "text-green-400",
    },
    orange: {
      bg: "from-orange-500/10 to-orange-600/5",
      border: "border-orange-500/20",
      text: "text-orange-400",
    },
    red: {
      bg: "from-red-500/10 to-red-600/5",
      border: "border-red-500/20",
      text: "text-red-400",
    },
    purple: {
      bg: "from-purple-500/10 to-purple-600/5",
      border: "border-purple-500/20",
      text: "text-purple-400",
    },
    cyan: {
      bg: "from-cyan-500/10 to-cyan-600/5",
      border: "border-cyan-500/20",
      text: "text-cyan-400",
    },
    emerald: {
      bg: "from-emerald-500/10 to-emerald-600/5",
      border: "border-emerald-500/20",
      text: "text-emerald-400",
    },
  };

  const colors = colorConfig[color] || colorConfig.blue;

  return (
    <div
      className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-xl p-3 sm:p-4 hover:scale-105 transition-transform duration-200`}
    >
      <div className="flex items-start justify-between mb-3">
        <span
          className={`text-xs font-medium uppercase tracking-wide ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
        >
          {title}
        </span>
        <Icon size={16} className={colors.text} />
      </div>
      <div
        className={`text-xl sm:text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
      >
        {value}
      </div>
      {subtitle && (
        <div className={`text-sm ${colors.text} mt-1 font-medium`}>
          {subtitle}
        </div>
      )}
    </div>
  );
};

// ============================================
// REPORT CARD COMPONENT
// ============================================

const ReportCard = ({ report, onViewDetails, onExport, isDarkMode }) => {
  const data = report.results?.data || [];

  const getMemberName = (row) => {
    const groupBy = report.config?.groupBy;

    switch (groupBy) {
      case "ownerId":
        return row.owner?.name || row["owner.name"] || "Unknown";
      case "accountId":
        return row.account?.name || row["account.name"] || "Unknown";
      case "type":
        return row.type || "Unknown";
      case "status":
        return row.status || "Unknown";
      case "closedDate":
      default:
        return (
          row.owner?.name ||
          row["owner.name"] ||
          row.type ||
          row.status ||
          "Unknown"
        );
    }
  };

  const getMetrics = (row) => {
    const metrics = [];

    if (row["Cases Closed"]) {
      metrics.push({
        label: "Closed",
        value: formatNumber(row["Cases Closed"]),
        color: "text-green-400",
      });
    }
    if (row["Total Cases"]) {
      metrics.push({
        label: "Total",
        value: formatNumber(row["Total Cases"]),
        color: "text-blue-400",
      });
    }
    if (row["High Priority"]) {
      metrics.push({
        label: "High",
        value: formatNumber(row["High Priority"]),
        color: "text-orange-400",
      });
    }
    if (row["Medium Priority"]) {
      metrics.push({
        label: "Med",
        value: formatNumber(row["Medium Priority"]),
        color: "text-yellow-400",
      });
    }
    if (row["Low Priority"]) {
      metrics.push({
        label: "Low",
        value: formatNumber(row["Low Priority"]),
        color: "text-gray-400",
      });
    }
    if (row["Avg Resolution Days"] !== undefined) {
      metrics.push({
        label: "Avg Days",
        value: formatDecimal(row["Avg Resolution Days"]),
        color: "text-cyan-400",
      });
    }
    if (row["Avg Age Days"] !== undefined) {
      metrics.push({
        label: "Avg Age",
        value: formatDecimal(row["Avg Age Days"]),
        color: "text-purple-400",
      });
    }
    if (row["1 Day Resolution %"] !== undefined) {
      metrics.push({
        label: "1 Day %",
        value: formatPercent(row["1 Day Resolution %"]),
        color: "text-emerald-400",
      });
    }
    if (row["3 Day Resolution %"] !== undefined) {
      metrics.push({
        label: "3 Day %",
        value: formatPercent(row["3 Day Resolution %"]),
        color: "text-emerald-400",
      });
    }

    return metrics;
  };

  return (
    <div
      className={`rounded-xl border overflow-hidden hover:border-gray-700 transition-colors ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-start justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <div className="flex-1 min-w-0 pr-4">
          <h3
            className={`text-base font-semibold truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {report.name}
          </h3>
          <p
            className={`text-xs mt-1 line-clamp-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {report.description}
          </p>
        </div>
        <button
          onClick={onExport}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
          title="Export to CSV"
        >
          <Download size={16} />
        </button>
      </div>

      {/* Content */}
      <div className="p-3 sm:p-4 max-h-80 overflow-y-auto">
        {data.length === 0 ? (
          <div
            className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No data available</p>
          </div>
        ) : (
          <div className="space-y-2">
            {data.map((row, idx) => {
              const metrics = getMetrics(row);
              return (
                <button
                  key={idx}
                  onClick={() => onViewDetails(row)}
                  className={`w-full text-left p-3 rounded-lg transition-colors group ${isDarkMode ? "bg-gray-900/40 hover:bg-gray-800/60" : "bg-gray-50 hover:bg-gray-100"}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`font-medium truncate flex-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                    >
                      {getMemberName(row)}
                    </span>
                    <ChevronRight
                      size={16}
                      className={`ml-2 flex-shrink-0 ${isDarkMode ? "text-gray-600 group-hover:text-gray-400" : "text-gray-400 group-hover:text-gray-600"}`}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {metrics.map((metric, mIdx) => (
                      <span key={mIdx} className="text-xs">
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          {metric.label}:
                        </span>{" "}
                        <span className={metric.color}>{metric.value}</span>
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with totals */}
      {report.results?.aggregates &&
        Object.keys(report.results.aggregates).length > 0 && (
          <div
            className={`p-3 border-t ${isDarkMode ? "bg-gray-900/60 border-gray-800" : "bg-gray-50 border-gray-200"}`}
          >
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
              {Object.entries(report.results.aggregates).map(([key, value]) => (
                <span key={key}>
                  <span
                    className={isDarkMode ? "text-gray-500" : "text-gray-400"}
                  >
                    {key}:
                  </span>{" "}
                  <span
                    className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {key.includes("Days") || key.includes("Age")
                      ? formatDecimal(value)
                      : formatNumber(value)}
                  </span>
                </span>
              ))}
            </div>
          </div>
        )}
    </div>
  );
};

// ============================================
// DAY BY DAY REPORT COMPONENT
// ============================================

const DayByDayReport = ({ report, onExport, isDarkMode }) => {
  const [expanded, setExpanded] = useState(true);
  const data = report.results?.data || [];

  // Group by date
  const groupedByDate = data.reduce((acc, row) => {
    const date = row.closedDate;
    if (!acc[date]) acc[date] = [];
    acc[date].push(row);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupedByDate).sort(
    (a, b) => new Date(b) - new Date(a),
  );

  return (
    <div
      className={`rounded-xl border overflow-hidden ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-white hover:text-gray-300" : "text-gray-900 hover:text-gray-600"}`}
        >
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <h3 className="text-base font-semibold">{report.name}</h3>
        </button>
        <button
          onClick={onExport}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
        >
          <Download size={16} />
        </button>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-3 sm:p-4 max-h-96 overflow-y-auto">
          {sortedDates.length === 0 ? (
            <div
              className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Calendar size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No cases closed in this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedDates.map((date) => {
                const dayClosures = groupedByDate[date];
                const totalForDay = dayClosures.reduce(
                  (sum, c) => sum + (parseInt(c["Cases Closed"], 10) || 0),
                  0,
                );

                return (
                  <div
                    key={date}
                    className={`border rounded-lg overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                  >
                    <div
                      className={`px-4 py-2 flex items-center justify-between ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                    >
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {formatDate(date)}
                      </span>
                      <span className="text-sm text-green-400 font-medium">
                        {totalForDay} closed
                      </span>
                    </div>
                    <div
                      className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                    >
                      {dayClosures.map((closure, idx) => (
                        <div
                          key={idx}
                          className={`px-4 py-2 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                        >
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {closure["owner.name"] || "Unknown"}
                          </span>
                          <span
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {closure["Cases Closed"]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// WEEK BY WEEK REPORT COMPONENT
// ============================================

const WeekByWeekReport = ({ report, onExport, isDarkMode }) => {
  const [expanded, setExpanded] = useState(true);
  const data = report.results?.data || [];

  // Group by weekStart
  const groupedByWeek = data.reduce((acc, row) => {
    const key = row.weekStart;
    if (!acc[key])
      acc[key] = {
        weekStart: row.weekStart,
        weekEnd: row.weekEnd,
        closures: [],
      };
    acc[key].closures.push(row);
    return acc;
  }, {});

  const sortedWeeks = Object.keys(groupedByWeek).sort(
    (a, b) => new Date(b) - new Date(a),
  );

  return (
    <div
      className={`rounded-xl border overflow-hidden ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
    >
      {/* Header */}
      <div
        className={`p-4 border-b flex items-center justify-between ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
      >
        <button
          onClick={() => setExpanded(!expanded)}
          className={`flex items-center gap-2 transition-colors ${isDarkMode ? "text-white hover:text-gray-300" : "text-gray-900 hover:text-gray-600"}`}
        >
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          <h3 className="text-base font-semibold">{report.name}</h3>
        </button>
        <button
          onClick={onExport}
          className={`p-2 rounded-lg transition-colors ${isDarkMode ? "text-gray-400 hover:text-white hover:bg-gray-800" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
        >
          <Download size={16} />
        </button>
      </div>

      {/* Content */}
      {expanded && (
        <div className="p-3 sm:p-4 max-h-96 overflow-y-auto">
          {sortedWeeks.length === 0 ? (
            <div
              className={`text-center py-8 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <Calendar size={32} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">No cases closed in this period</p>
            </div>
          ) : (
            <div className="space-y-4">
              {sortedWeeks.map((weekKey) => {
                const { weekStart, weekEnd, closures } = groupedByWeek[weekKey];
                const totalForWeek = closures.reduce(
                  (sum, c) => sum + (parseInt(c["Cases Closed"], 10) || 0),
                  0,
                );

                return (
                  <div
                    key={weekKey}
                    className={`border rounded-lg overflow-hidden ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                  >
                    <div
                      className={`px-4 py-2 flex items-center justify-between ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                    >
                      <span
                        className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {formatDate(weekStart)} — {formatDate(weekEnd)}
                      </span>
                      <span className="text-sm text-green-400 font-medium">
                        {totalForWeek} closed
                      </span>
                    </div>
                    <div
                      className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                    >
                      {closures.map((closure, idx) => (
                        <div
                          key={idx}
                          className={`px-4 py-2 flex items-center justify-between transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                        >
                          <span
                            className={`text-sm ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                          >
                            {closure["owner.name"] || "Unknown"}
                          </span>
                          <span
                            className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {closure["Cases Closed"]}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================
// DETAIL MODAL COMPONENT
// ============================================

const DetailModal = ({ report, onClose, isDarkMode }) => {
  const memberName =
    report.memberData?.owner?.name ||
    report.memberData?.["owner.name"] ||
    report.memberData?.account?.name ||
    report.memberData?.["account.name"] ||
    report.memberData?.type ||
    report.memberData?.status ||
    "Details";

  const detailRecords = report.detailRecords || [];

  // Stats from memberData
  const stats = [];
  if (report.memberData?.["Cases Closed"]) {
    stats.push({
      label: "Cases Closed",
      value: report.memberData["Cases Closed"],
      color: "green",
    });
  }
  if (report.memberData?.["Total Cases"]) {
    stats.push({
      label: "Total Cases",
      value: report.memberData["Total Cases"],
      color: "blue",
    });
  }
  if (report.memberData?.["High Priority"]) {
    stats.push({
      label: "High Priority",
      value: report.memberData["High Priority"],
      color: "orange",
    });
  }
  if (report.memberData?.["Avg Resolution Days"] !== undefined) {
    stats.push({
      label: "Avg Days",
      value: formatDecimal(report.memberData["Avg Resolution Days"]),
      color: "cyan",
    });
  }

  const colorConfig = {
    blue: "bg-blue-500/10 border-blue-500/20",
    green: "bg-green-500/10 border-green-500/20",
    orange: "bg-orange-500/10 border-orange-500/20",
    cyan: "bg-cyan-500/10 border-cyan-500/20",
  };

  const formatCaseStatus = (status) => {
    return status || "Unknown";
  };

  const getCaseStatusBadgeClass = (status) => {
    const classMap = {
      New: "bg-blue-500/10 border-blue-500/20",
      "In Progress": "bg-cyan-500/10 border-cyan-500/20",
      "On Hold": "bg-yellow-500/10 border-yellow-500/20",
      Escalated: "bg-red-500/10 border-red-500/20",
      Closed: "bg-green-500/10 border-green-500/20",
    };
    return classMap[status] || "bg-gray-500/10 border-gray-500/20";
  };

  const getPriorityBadgeClass = (priority) => {
    const classMap = {
      High: "bg-red-500/10 border-red-500/20 text-red-400",
      Medium: "bg-yellow-500/10 border-yellow-500/20 text-yellow-400",
      Low: "bg-gray-500/10 border-gray-500/20 text-gray-400",
    };
    return (
      classMap[priority] || "bg-gray-500/10 border-gray-500/20 text-gray-400"
    );
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div
        className={`rounded-t-2xl sm:rounded-2xl border max-w-6xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col shadow-2xl ${isDarkMode ? "bg-[#0d0f14] border-gray-800" : "bg-white border-gray-200"}`}
      >
        {/* Header */}
        <div
          className={`p-4 sm:p-6 border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <div className="flex items-start justify-between">
            <div>
              <h2
                className={`text-lg sm:text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {report.name}
              </h2>
              <p
                className={
                  isDarkMode ? "text-gray-400 mt-1" : "text-gray-500 mt-1"
                }
              >
                {memberName} - Detail Records
              </p>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${isDarkMode ? "hover:bg-gray-800" : "hover:bg-gray-100"}`}
            >
              <X
                size={20}
                className={isDarkMode ? "text-gray-400" : "text-gray-500"}
              />
            </button>
          </div>

          {/* Summary Stats */}
          {stats.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
              {stats.map((stat, idx) => (
                <div
                  key={idx}
                  className={`${colorConfig[stat.color]} border rounded-lg p-3`}
                >
                  <div
                    className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                  >
                    {stat.label}
                  </div>
                  <div
                    className={`text-lg font-bold mt-1 ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Detail Records */}
        <div className="flex-1 overflow-auto p-3 sm:p-6">
          {detailRecords.length === 0 ? (
            <div
              className={`text-center py-16 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
            >
              <FileText size={48} className="mx-auto mb-4 opacity-50" />
              <p>No detail records available</p>
              <p className="text-sm mt-1">Click refresh to try loading again</p>
            </div>
          ) : (
            <>
              {/* Mobile card list — hidden on md+ */}
              <div className="flex flex-col gap-3 md:hidden">
                {detailRecords.map((record, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg border p-3 text-sm ${isDarkMode ? "border-gray-800 bg-gray-900/40" : "border-gray-200 bg-gray-50"}`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <span
                        className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}
                      >
                        {record.caseNumber || "-"}
                      </span>
                      <div className="flex gap-1 flex-shrink-0">
                        <span
                          className={`px-2 py-0.5 rounded border text-xs whitespace-nowrap ${getCaseStatusBadgeClass(record.status)}`}
                        >
                          {formatCaseStatus(record.status)}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded border text-xs whitespace-nowrap ${getPriorityBadgeClass(record.priority)}`}
                        >
                          {record.priority || "-"}
                        </span>
                      </div>
                    </div>
                    <p
                      className={`mb-2 ${isDarkMode ? "text-gray-200" : "text-gray-700"}`}
                    >
                      {record.subject || "Untitled"}
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          Account:{" "}
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {record.account?.name ||
                            record["account.name"] ||
                            "-"}
                        </span>
                      </div>
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          Owner:{" "}
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {record.owner?.name || record["owner.name"] || "-"}
                        </span>
                      </div>
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          Type:{" "}
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {record.type || "-"}
                        </span>
                      </div>
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          Created:{" "}
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {formatDate(record.createdAt)}
                        </span>
                      </div>
                      <div>
                        <span
                          className={
                            isDarkMode ? "text-gray-500" : "text-gray-400"
                          }
                        >
                          Closed:{" "}
                        </span>
                        <span
                          className={
                            isDarkMode ? "text-gray-300" : "text-gray-600"
                          }
                        >
                          {formatDate(record.closedDate)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table — hidden on mobile */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead
                    className={`sticky top-0 ${isDarkMode ? "bg-gray-900/60" : "bg-gray-50"}`}
                  >
                    <tr
                      className={`border-b ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
                    >
                      {[
                        "Case Number",
                        "Subject",
                        "Account",
                        "Owner",
                        "Status",
                        "Priority",
                        "Type",
                        "Created",
                        "Closed",
                      ].map((heading) => (
                        <th
                          key={heading}
                          className={`text-left py-3 px-4 font-medium ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                        >
                          {heading}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody
                    className={`divide-y ${isDarkMode ? "divide-gray-800" : "divide-gray-200"}`}
                  >
                    {detailRecords.map((record, idx) => (
                      <tr
                        key={idx}
                        className={`transition-colors ${isDarkMode ? "hover:bg-gray-800/30" : "hover:bg-gray-50"}`}
                      >
                        <td className="py-3 px-4">
                          <span
                            className={`font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}
                          >
                            {record.caseNumber || "-"}
                          </span>
                        </td>
                        <td
                          className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {record.subject || "Untitled"}
                        </td>
                        <td
                          className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {record.account?.name ||
                            record["account.name"] ||
                            "-"}
                        </td>
                        <td
                          className={`py-3 px-4 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {record.owner?.name || record["owner.name"] || "-"}
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded border text-xs whitespace-nowrap ${getCaseStatusBadgeClass(record.status)}`}
                          >
                            {formatCaseStatus(record.status)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span
                            className={`px-2 py-1 rounded border text-xs whitespace-nowrap ${getPriorityBadgeClass(record.priority)}`}
                          >
                            {record.priority || "-"}
                          </span>
                        </td>
                        <td
                          className={`py-3 px-4 text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {record.type || "-"}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {formatDate(record.createdAt)}
                        </td>
                        <td
                          className={`py-3 px-4 text-xs ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
                        >
                          {formatDate(record.closedDate)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div
          className={`p-4 border-t flex justify-between items-center ${isDarkMode ? "border-gray-800" : "border-gray-200"}`}
        >
          <span
            className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
          >
            {detailRecords.length} record{detailRecords.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={onClose}
            className={`px-6 py-2 rounded-lg transition-colors ${isDarkMode ? "bg-gray-800 hover:bg-gray-700 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-900"}`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CasesDashboard;
